import { UseMutationOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import { requestApi } from "api/TenantSlug/Request/api";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "types/models.ts";

import GetUserRequestsRequest = App.Http.Requests.UserRequest.GetUserRequestsRequest;
import GetUserRequestRequest = App.Http.Requests.UserRequest.GetUserRequestRequest;
import StoreUserRequestRequest = App.Http.Requests.UserRequest.StoreUserRequestRequest;
import UpdateUserRequestRequest = App.Http.Requests.UserRequest.UpdateUserRequestRequest;
import GetUserRequestsResponse = App.Http.Responses.UserRequest.GetUserRequestsResponse;
import GetUserRequestResponse = App.Http.Responses.UserRequest.GetUserRequestResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import ExportResponse = App.Http.Responses.ExportResponse;

// Query to get a list of requests
export const useGetRequestsQuery = (request: GetUserRequestsRequest, options = {}) => {
    return useQueryApi<GetUserRequestsResponse>({
        queryKey: ["getRequests", request],
        queryFn: () => requestApi.index(request),
        ...options,
    });
};

// Query to get a specific request by ID
export const useGetRequestQuery = (requestId: string, request: GetUserRequestRequest, options = {}) => {
    return useQueryApi<GetUserRequestResponse>({
        queryKey: ["getRequest", requestId],
        queryFn: () => requestApi.show(requestId, request),
        ...options,
    });
};

// Mutation to store a new request
export const useStoreRequestMutation = (
    options: UseMutationOptions<GetUserRequestResponse, AxiosError<ApiErrorResponse>, StoreUserRequestRequest> = {}
) => {
    return useMutationApi<GetUserRequestResponse, StoreUserRequestRequest>({
        mutationKey: ["storeRequest"],
        mutationFn: (request: StoreUserRequestRequest) => requestApi.store(request),
        ...options,
    });
};

// Mutation to update an existing request
export const useUpdateRequestMutation = (options = {}) => {
    return useMutationApi<GetUserRequestResponse, { requestId: string; request: UpdateUserRequestRequest }>({
        mutationKey: ["updateRequest"],
        mutationFn: ({ requestId, request }) => requestApi.update(requestId, request),
        ...options,
    });
};

// Mutation to cancel a pending request (e.g. an admin cancelling a tenant invite)
export const useDestroyRequestMutation = (
    options: UseMutationOptions<null, AxiosError<ApiErrorResponse>, string> = {}
) => {
    return useMutationApi<null, string>({
        mutationKey: ["destroyRequest"],
        mutationFn: (requestId: string) => requestApi.destroy(requestId),
        ...options,
    });
};

export const useExportRequestsMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["exportRequests"],
        mutationFn: (request) => requestApi.export(request),
        ...options,
    });
};
